/**
 * Company: SPHMedia
 * Description: [Detects availability of catfish ad unit]
 */

import { isClientSide } from "@helper/utils";
import { useEffect, useRef, useState } from "react";

type useCatfishObserverData = {
  isCatfishAvailable: boolean;
};

type useCatfishObserverProps = {
  isCatfishEnabled: boolean | undefined;
  isMobile: boolean;
};

/**
 * Custom React Hook: useCatfishObserver
 * Description: [Hook to detect availability and closure of catfish ad unit]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useCatfishObserver({
  isCatfishEnabled,
  isMobile,
}: useCatfishObserverProps): useCatfishObserverData {
  const [isCatfishAvailable, setIsCatfishAvailable] = useState<boolean>(false);

  const closeObserverRef = useRef<MutationObserver | null>(null);
  const isRenderedRef = useRef(false);

  useEffect(() => {
    if (!isClientSide) return;

    const el = document.getElementById("dfp-ad-catfish");
    if (!el) return;

    // Function to attach close observer once element is present
    const attachCloseObserver = () => {
      if (closeObserverRef.current) return; // already attached

      closeObserverRef.current = new MutationObserver(() => {
        if (!isRenderedRef.current) return;

        const iframe = el.querySelector("iframe");

        const isClosed =
          !el.offsetHeight ||
          !el.offsetWidth ||
          (iframe && (iframe.offsetHeight === 0 || iframe.offsetWidth === 0)) ||
          getComputedStyle(el).display === "none";

        if (isClosed) {
          setIsCatfishAvailable(false);
          closeObserverRef.current?.disconnect();
          closeObserverRef.current = null;
        }
      });

      closeObserverRef.current.observe(el, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    };

    const onSlotRenderEnded = (
      event: googletag.events.SlotRenderEndedEvent,
    ) => {
      if (event.slot.getSlotElementId() !== "dfp-ad-catfish") return;

      if (!event.isEmpty) {
        isRenderedRef.current = true;
        setIsCatfishAvailable(true);
        attachCloseObserver();
      }
    };

    if ((window.googletag as typeof googletag | undefined)?.pubads) {
      try {
        (window.googletag as typeof googletag)
          .pubads()
          .addEventListener("slotRenderEnded", onSlotRenderEnded);
      } catch {
        // silent fail – GPT not ready
      }
    }

    return () => {
      closeObserverRef.current?.disconnect();
      closeObserverRef.current = null;

      if ((window.googletag as typeof googletag | undefined)?.pubads) {
        try {
          (window.googletag as typeof googletag)
            .pubads()
            .removeEventListener("slotRenderEnded", onSlotRenderEnded);
        } catch {
          // noop
        }
      }
    };
  }, [isCatfishEnabled, isMobile]);

  return { isCatfishAvailable };
}
